import type { MinMax } from '@iheartjane/types';

/**
 * URLSearchParams.has accepts a second parameter per MDN, which should replace this function.
 * The feature is currently under development and doesn't work in Safari yet.
 * @see https://caniuse.com/mdn-api_urlsearchparams_has
 */
export const searchParamsHaveKeyValuePair = (
  searchParams: URLSearchParams,
  key: string,
  value: string
) => {
  const values = searchParams.getAll(key);
  return values.some((val) => val === value);
};

/**
 * URLSearchParams.delete accepts a second parameter per MDN, which should replace this function.
 * The feature is currently under development and doesn't work in Safari yet.
 * @see https://caniuse.com/mdn-api_urlsearchparams_delete
 */
export const deleteKeyValuePairFromSearchParams = (
  searchParams: URLSearchParams,
  key: string,
  value: string
) => {
  const values = searchParams.getAll(key);
  searchParams.delete(key);

  const valuesToKeep = values.filter((val) => val !== value);
  valuesToKeep.forEach((newValue) => {
    // Add back values that are not an exact match
    searchParams.append(key, newValue);
  });
};

export const searchParamsFromFilterKeyRange = (
  searchParams: URLSearchParams,
  key: string,
  { max, min }: MinMax
): URLSearchParams => {
  searchParams.set(key, `${min}:${max}`);
  return searchParams;
};

export const filterRangeFromSearchParams = (
  searchParams: URLSearchParams,
  key: string
): Partial<MinMax> => {
  const value = searchParams.get(key);
  const [min, max] = (value ?? '').split(':');

  return {
    max: max ? parseInt(max, 10) : undefined,
    min: min ? parseInt(min, 10) : undefined,
  };
};

export const searchParamsFromFilterKey = (
  searchParams: URLSearchParams,
  key: string,
  option: string,
  value: boolean
): URLSearchParams => {
  if (value && !searchParamsHaveKeyValuePair(searchParams, key, option)) {
    searchParams.append(key, option);

    // if it's a prefixed option
    const [category, subcategory] = option.split(':');
    if (subcategory) {
      deleteKeyValuePairFromSearchParams(searchParams, key, category);
    }
  } else {
    // delete the option -- "edible"
    deleteKeyValuePairFromSearchParams(searchParams, key, option);

    // delete any options that are prefixed with option -- "edible:Baked Goods"
    searchParams
      .getAll(key)
      .filter((i) => i.startsWith(`${option}:`))
      .forEach((i: string) =>
        deleteKeyValuePairFromSearchParams(searchParams, 'category', i)
      );
  }
  return searchParams;
};

export const getFilterStatusInSearchParams = (
  searchParams: URLSearchParams,
  key: string,
  option: string
): 'selected' | 'not-selected' | 'children-selected' => {
  const options = searchParams.getAll(key);

  const exactMatch = options.filter((i) => i === option).length > 0;
  if (exactMatch) return 'selected';

  const fuzzyMatch =
    options.filter((i) => i.startsWith(`${option}:`)).length > 0;
  if (fuzzyMatch) return 'children-selected';

  return 'not-selected';
};

export const buildRangeWithBounds = (
  range: MinMax,
  value?: Partial<MinMax>
) => {
  return {
    max: value?.max || range.max,
    min: value?.min || range.min,
  };
};

const SINGULAR_FILTER_KEYS: Record<string, string> = {
  activities: 'activity',
  brands: 'brand',
  categories: 'category',
  cbdPotencies: 'cbdPotency',
  feelings: 'feeling',
  lineages: 'lineage',
  potencies: 'potency',
  prices: 'price',
  ratings: 'rating',
  specials: 'special',
  terpenes: 'terpene',
  thcPotencies: 'thcPotency',
  weights: 'weight',
};

export const singularFilterKey = (key: string): string =>
  SINGULAR_FILTER_KEYS[key] ?? key;
